import { validateNewEmailExistsApi } from "@/api/services/main/auth";
import { useGetUserSettingInfo } from "@/api/services/main/user-setting";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { ChangeEmailForm } from "@/features/user-setting/components/setting-form/ChangeEmailForm";
import { ChangeEmailConfirmPage } from "@/features/user-setting/pages/ChangeEmailConfirmPage";
import { useGetUserSettingEmailSchema } from "@/features/user-setting/schema/userSettingEmailSchema";
import { TUserSettingEmailForm } from "@/features/user-setting/types/UserSettingEmailForm";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { showError, setErrorForm } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";

type TUserSettingChangeEmailPageProps = {};

export const UserSettingChangeEmailPage: FC<TUserSettingChangeEmailPageProps> = () => {
  const { data: userSettingResult, isLoading } = useGetUserSettingInfo();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const emailParam = searchParams.get("email");
  const navigate = useNavigate();
  const { user } = useInitSetup();
  const isUser = user?.isUser ?? true;
  const userSettingEmailSchema = useGetUserSettingEmailSchema({
    currentEmail: userSettingResult?.data.userEmail ?? "",
    isUser,
  });

  const methods = useForm<TUserSettingEmailForm>({
    defaultValues: emailParam ? { email: emailParam } : {},
    resolver: yupResolver(userSettingEmailSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const handleConfirm = async (data: TUserSettingEmailForm) => {
    try {
      const response = await validateNewEmailExistsApi({ email: data.email });
      if (response.data) {
        setIsConfirmed(true);
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleBackToForm = () => {
    setIsConfirmed(false);
    if (emailParam) {
      // Came in via URL param → no form state; fallback to user setting detail
      navigate(AppRoutes.userSettingDetail);
      return;
    }
    searchParams.delete("email");
    setSearchParams(searchParams);
  };

  if (isLoading) return <LoadingPage />;

  if (!isConfirmed && !emailParam) {
    return (
      <ChangeEmailForm
        methods={methods}
        currentEmail={userSettingResult?.data.userEmail ?? ""}
        handleConfirm={handleConfirm}
        schema={userSettingEmailSchema}
      />
    );
  }

  return (
    <ChangeEmailConfirmPage
      methods={methods}
      currentEmail={userSettingResult?.data.userEmail ?? ""}
      newEmail={emailParam ?? methods.getValues("email")}
      onBack={handleBackToForm}
    />
  );
};
